import React from "react";
import i18n from "i18next";
import Helmet from "react-helmet";
import { isExperienceEditorActive, dataApi } from "@sitecore-jss/sitecore-jss-react";
import CompatibleSitecoreContextFactory from "./lib/SitecoreContextFactory";
import { dataFetcher } from "./dataFetcher";
import config from "./temp/config";
import Layout from "./layouts/Layout";
import LayoutEmail from "./layouts/Layout-Email";
import UseQuery from "./utils/useQuery";
import NotFound from "./NotFound";
import LayoutModal from "./layouts/Layout-Modal";

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

let ssrInitialState = null;

export default class RouteHandler extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notFound: true,
      routeData: ssrInitialState, // null when client-side rendering
      defaultLanguage: config.defaultLanguage,
      noHeaderFooter: false
    };

    if (ssrInitialState && ssrInitialState.sitecore && ssrInitialState.sitecore.route) {
      // set the initial sitecore context data if we got SSR initial state
      CompatibleSitecoreContextFactory.setSitecoreContext({
        route: ssrInitialState.sitecore.route,
        itemId: ssrInitialState.sitecore.route.itemId,
        ...ssrInitialState.sitecore.context
      });
    }

    // route data from react-router - if route was resolved, it's not a 404
    if (props.route !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (ssrInitialState && (!ssrInitialState.sitecore || !ssrInitialState.sitecore.route)) {
      this.state.notFound = true;
    }
    // if (!props.match.params.lang) {
    //   this.state.notFound = true;
    // }

    // if we have an SSR state, and that state has language data, set the current language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /de-DE) will override this default; this is for home.
    if (ssrInitialState && ssrInitialState.context && ssrInitialState.context.language) {
      this.state.defaultLanguage = ssrInitialState.context.language;
    }

    // once we initialize the route handler, we've "used up" the SSR data,
    // if it existed, so we want to clear it now that it's in react state.
    // future route changes that might destroy/remount this component should ignore any SSR data.
    // EXCEPTION: Unless we are still SSR-ing. Because SSR can re-render the component twice
    // (once to find GraphQL queries that need to run, the second time to refresh the view with
    // GraphQL query results)
    // We test for SSR by checking for Node-specific process.env variable.
    if (typeof window !== "undefined") {
      ssrInitialState = null;
    }

    this.componentIsMounted = false;
    this.languageIsChanging = false;
    // Route Data is changing
    this.routeDataIsChanging = true;
    // tell i18next to sync its current language with the route language
    this.updateLanguage();
  }

  componentDidMount() {
    // if no existing routeData is present (from SSR), get Layout Service fetching the route data
    if (!this.state.routeData) {
      this.updateRouteData();
    }
    let noHeaderFooter = this.state.noHeaderFooter;

    const query = UseQuery();
    if (query.noHeaderFooter) {
      noHeaderFooter = true;
    }
    this.setState({
      noHeaderFooter: noHeaderFooter
    });
    this.loop();
    this.postMessageIframe();
    this.componentIsMounted = true;
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }
  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.route.match.url;
    const newRoute = this.props.route.match.url;
    this.routeDataIsChanging = true;
    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }
    // if in experience editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isExperienceEditorActive()) {
      window.location.assign(newRoute);
      return;
    }
    this.updateLanguage();
    this.updateRouteData();
  }
  postMessageIframe = () => {
    let time1 = null,
      b_pageH = 0,
      timenum = 0;
    const query = UseQuery();
    if (!query.noHeaderFooter) {
      return false;
    }
    if (document.querySelector("body")) {
      document.querySelector("body").classList.add("show-modal");
    }
    time1 = setInterval(function() {
      b_pageH = document.querySelector("html").scrollHeight;
      if (timenum > 30) {
        clearInterval(time1);
        time1 = null;
        if (document.querySelector("body")) {
          document.querySelector("body").classList.remove("show-modal");
        }
      } else {
        timenum++;
        window.parent.postMessage(b_pageH, "/");
      }
    }, 500);
  };
  loop = () => {
    // h1 squeeze animation
    let elementsToShow = document.querySelectorAll(
      ".show-on-scroll,.type-h1, .type-h1-outlined .type-h1-urban-jundle-to-plum-horisontal, .type-h1-urban-jundle-to-plum-vertical, .type-h1-plum-to-urban-jundle-diagonal, .type-h1-purple-to-green-horisontal, .type-h1-green-to-purple-vertical, .type-h1-green-to-purple-diagonal"
    );
    let arr = [];
    let that = this;
    arr.forEach.call(elementsToShow, function(element) {
      if (that.isElementInViewport(element)) {
        element.classList.add("is-visible");
      }
    });
    window.requestAnimationFrame(this.loop);
  };
  isElementInViewport(el) {
    let rect = el.getBoundingClientRect();
    return (
      (rect.top <= 0 && rect.bottom >= 0) || (rect.bottom >= (window.innerHeight || document.documentElement.clientHeight) && rect.top <= (window.innerHeight || document.documentElement.clientHeight)) || (rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight))
    );
  }
  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateRouteData() {
    let sitecoreRoutePath = this.props.route.match.params.sitecoreRoute || "/";
    if (!sitecoreRoutePath.startsWith("/")) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    const language = this.props.route.match.params.lang || this.state.defaultLanguage || "en";
    // get the route data for the new route
    getRouteData(sitecoreRoutePath, language).then(routeData => {
      this.routeDataIsChanging = false;
      if (routeData !== null && routeData.sitecore && routeData.sitecore.route) {
        // set the sitecore context data and push the new route
        CompatibleSitecoreContextFactory.setSitecoreContext({
          route: routeData.sitecore.route,
          itemId: routeData.sitecore.route.itemId,
          ...routeData.sitecore.context
        });
        this.setState({ routeData, notFound: false });
      } else {
        if (routeData && routeData.status === 404) {
          // csr 404 redirect
          this.setState({ routeData, notFound: true });
          // this.props.route.history.push(`/${language}/404`);
        } else this.props.route.history.push(`/${language}/500`);
      }
    });
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage() {
    const newLanguage = this.props.route.match.params.lang || this.state.defaultLanguage;

    if (i18n.language !== newLanguage) {
      this.languageIsChanging = true;

      i18n.changeLanguage(newLanguage, () => {
        this.languageIsChanging = false;

        // if the component is not mounted, we don't care
        // (next time it mounts, it will render with the right language context)
        if (this.componentIsMounted) {
          // after we change the i18n language, we need to force-update React,
          // since otherwise React won't know that the dictionary has changed
          // because it is stored in i18next state not React state
          this.forceUpdate();
        }
      });
    }
  }

  render() {
    const { routeData, noHeaderFooter, notFound } = this.state;
    // no route data for the current route in Sitecore - show not found component.
    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    if (notFound) {
      return (
        <div>
          <Helmet>
            <title>{i18n.t("Page not found")}</title>
          </Helmet>
          <NotFound lang={this.props.route.match.params.lang || "en"} history={this.props.route.history} />
        </div>
      );
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!routeData || this.languageIsChanging) {
      return null;
    }
    let lang = this.props.route.match.params.lang || "en";
    lang = lang.toLowerCase();
    // Render the app's root structural layout
    let layoutid = routeData.sitecore.route && routeData.sitecore.route.layoutId;
    const layoutProps = {
      route: routeData.sitecore.route,
      context: routeData.sitecore.context,
      path: this.props.route.location.pathname,
      lang,
      server: this.props.server,
      match: this.props.match
    };
    if (layoutid === "184390C8-0E44-4474-88BB-B0FDD78A9F3F".toLowerCase()) {
      return <LayoutEmail {...layoutProps} noHeaderFooter={noHeaderFooter} />;
    } else if (layoutid === "31F77AE6-C895-4406-92C0-6345AD078830".toLowerCase()) {
      return <LayoutModal {...layoutProps} />;
    } else {
      return <Layout {...layoutProps} routeDataIsChanging={this.routeDataIsChanging} noHeaderFooter={noHeaderFooter} />;
    }
  }
}

/**
 * Sets the initial state provided by server-side rendering.
 * Setting this state will bypass initial route data fetch calls.
 * @param {object} ssrState
 */
export function setServerSideRenderingState(ssrState) {
  ssrInitialState = ssrState;
}

/**
 * Gets route data from Sitecore. This data is used to construct the component layout for a JSS route.
 * @param {string} route Route path to get data for (e.g. /about)
 * @param {string} language Language to get route data in (content language, e.g. 'en')
 */
function getRouteData(route, language) {
  const fetchOptions = {
    layoutServiceConfig: { host: config.sitecoreApiHost },
    querystringParams: { sc_lang: language, sc_apikey: config.sitecoreApiKey },
    fetcher: dataFetcher
  };

  return dataApi.fetchRouteData(route, fetchOptions).catch(error => {
    if (error.response) {
      return error.response;
    }
    console.error("Route data fetch error", error, error.response);

    return null;
  });
}
